import AwsS3, { type AwsBody } from '@uppy/aws-s3';
import Uppy from '@uppy/core';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useNotifications } from '../../stores/notificationStore';
import { useI18n } from 'vue-i18n';
import { useCompanionUploadSettings } from '../stores/companionUploadStore';
import { generateS3ObjectKey, signS3Request, type UppyMeta } from '../../composables/form/uppyS3';

export type { UppyMeta };

export interface UseCompanionUppyOptions {
  clearOnUnmount?: boolean;
  registerNotifications?: boolean;
  isGlobal?: boolean;
}

const uppy = new Uppy<UppyMeta, AwsBody>().use(AwsS3, {
  id: 'AwsS3Multipart',
  signRequest: signS3Request,
  generateObjectKey: generateS3ObjectKey,
});

export const useCompanionUppy = ({
  clearOnUnmount,
  registerNotifications,
  isGlobal,
}: UseCompanionUppyOptions = {}) => {
  if (registerNotifications) {
    const { t } = useI18n();
    const { addNotification, updateNotification } = useNotifications();
    const progressNotificationId = ref<string | null>(null);

    const onUpload = () => {
      if (progressNotificationId.value) return progressNotificationId.value;

      progressNotificationId.value = `upload-progress-${Date.now()}`;

      addNotification({
        id: progressNotificationId.value,
        title: t('notification.upload.progress.title'),
        message: t('notification.upload.progress.message'),
        type: 'info',
        progress: 0,
      });

      return progressNotificationId.value;
    };

    const onProgress = (progress: number) => {
      if (!progressNotificationId.value || progress === 0) return;
      updateNotification(progressNotificationId.value, { progress });
    };

    const onComplete = () => {
      if (!progressNotificationId.value) return;

      updateNotification(progressNotificationId.value, {
        title: t('notification.upload.complete.title'),
        message: t('notification.upload.complete.message'),
        type: 'success',
        progress: null,
      });
      progressNotificationId.value = null;
    };

    const onError = ({ message }: Error) => {
      if (!progressNotificationId.value) return;

      updateNotification(progressNotificationId.value, {
        title: t('notification.upload.failed.title'),
        message: t('notification.upload.failed.message', { message }),
        type: 'error',
        progress: null,
      });
      progressNotificationId.value = null;
    };

    onMounted(() => {
      uppy.on('upload', onUpload);
      uppy.on('progress', onProgress);
      uppy.on('complete', onComplete);
      uppy.on('error', onError);
    });

    onUnmounted(() => {
      uppy.off('upload', onUpload);
      uppy.off('progress', onProgress);
      uppy.off('complete', onComplete);
      uppy.off('error', onError);
    });
  }

  if (clearOnUnmount) {
    onUnmounted(() => {
      uppy.clear();
    });
  }

  if (isGlobal) {
    const { compressImages } = useCompanionUploadSettings();

    watch(
      compressImages,
      async (compress) => {
        if (compress) {
          const { default: Compressor } = await import('@uppy/compressor');
          uppy.use(Compressor);
          console.log('Compressor plugin added to Uppy');
        } else {
          const compressorPlugin = uppy.getPlugin('Compressor');
          if (compressorPlugin) uppy.removePlugin(compressorPlugin);
          console.log('Compressor plugin removed from Uppy');
        }
      },
      { immediate: true },
    );
  }

  return { uppy };
};
